import React from 'react';
import { CardLink } from './CardLink';
import { EntityIcon } from './taskIcons';
import { hexAlpha } from '../utils/statusColors';
import { formatDueDate } from '../utils/date';
import './ProjectCard.css';

/**
 * One project card (founder concept `projectCard()`), shared by the Projects
 * page and the workspace Dashboard. Field order: mark + health pill, name,
 * description, task progress, meta (lifecycle status · members | due date).
 *
 * Every number comes from the caller's API data. `stats` is a GET
 * /projects/portfolio row (server-computed, viewer-scoped counts); without it
 * the card shows no progress and no health pill rather than a guess.
 */
export interface ProjectCardProject {
    id: string;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    status: string;
    member_count?: number;
    due_date?: string;
}

export interface ProjectCardStats {
    total_tasks: number;
    done_tasks: number;
    blocked_tasks: number;
    overdue_tasks: number;
}

export const PROJECT_STATUS_LABELS: Record<string, string> = {
    planning: 'Planning', active: 'Active', on_hold: 'On hold',
    completed: 'Completed', archived: 'Archived',
};

// Health is a delivery signal; a finished or parked project has none.
const NO_HEALTH = new Set(['completed', 'archived']);

export function ProjectCard({ project, stats, actions }: {
    project: ProjectCardProject;
    stats?: ProjectCardStats;
    /** Extra controls (e.g. the options menu); rendered above the card link. */
    actions?: React.ReactNode;
}) {
    const color = project.color || '#14867A';
    const attention = !!stats && (stats.blocked_tasks > 0 || stats.overdue_tasks > 0);
    const showHealth = !!stats && !NO_HEALTH.has(project.status);
    const pct = stats && stats.total_tasks > 0 ? Math.round((stats.done_tasks / stats.total_tasks) * 100) : 0;
    const members = project.member_count;

    return (
        <CardLink to={`/projects/${project.id}`} ariaLabel={project.name} className="project-card pcard"
            style={{ '--card-color': color } as React.CSSProperties}>
            <div className="pcard-top">
                <span className="pcard-mark" data-testid="project-mark" style={{ background: hexAlpha(color, 0.13), color }} aria-hidden="true">
                    <EntityIcon icon={project.icon} size={18} />
                </span>
                <span className="pcard-top-end">
                    {showHealth && (
                        <span className={`pcard-pill pcard-pill--${attention ? 'attention' : 'ok'}`}>
                            {attention ? 'Needs attention' : 'On track'}
                        </span>
                    )}
                    {actions && <span className="pcard-actions">{actions}</span>}
                </span>
            </div>
            <h3 className="pcard-name">{project.name}</h3>
            <p className="pcard-desc">{project.description || 'No description'}</p>
            {stats && (stats.total_tasks === 0 ? (
                <p className="pcard-progress-label pcard-progress-label--empty">No accessible tasks</p>
            ) : (
                <div className="pcard-progress">
                    <div className="pcard-progress-label">
                        <span>{stats.done_tasks} / {stats.total_tasks} tasks completed</span>
                        <strong>{pct}%</strong>
                    </div>
                    <div className="pcard-track" aria-hidden="true"><span style={{ width: `${pct}%`, background: color }} /></div>
                </div>
            ))}
            <div className="pcard-meta">
                <span>
                    {PROJECT_STATUS_LABELS[project.status] ?? project.status}
                    {members !== undefined && ` · ${members} member${members === 1 ? '' : 's'}`}
                </span>
                {project.due_date && <span>Due {formatDueDate(project.due_date)}</span>}
            </div>
        </CardLink>
    );
}
